<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card v-if="!deleteCheck" class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="isEditForm" class="text-h5">Update trust</h2>
              <h2 v-else class="text-h5">Add new trust</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <div v-if="showExistingTrusts" class="text-center">
            <p>Select an existing trust or add a new trust</p>
            <div v-for="(existingTrust, index) in existingTrusts" :key="index">
              <v-btn
                block
                depressed
                class="existing-item-btn"
                :disabled="trustDisabled(existingTrust)"
                @click="selectExistingTrust(existingTrust)"
              >
                <div class="existing-item-text">
                  <span class="existing-item-name">{{
                    existingTrust.displayName
                  }}</span>
                  <span v-if="existingTrust.disabledMessage">
                    <br />
                    <span class="existing-item-extra-info">{{
                      existingTrust.disabledMessage
                    }}</span>
                  </span>
                </div>
                <div>
                  <v-icon class="plus-icon" color="primary">{{
                    icons.mdiPlusCircle
                  }}</v-icon>
                </div>
              </v-btn>
            </div>
          </div>

          <h3 v-if="showExistingTrusts" class="ml-1 mt-4 mb-6">Add a trust</h3>
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                id="trust_name"
                v-slot="{ errors }"
                name="trust_name"
                rules="required"
              >
                <TextField
                  v-model.trim="trust.name"
                  :label="'Trust name*'"
                  :errors="errors"
                  :name="'trust_name'"
                />
              </ValidationProvider>

              <ValidationProvider
                id="trust_incorporation_number"
                v-slot="{ errors }"
                name="trust_incorporation_number"
                rules="required"
              >
                <TextField
                  v-model.trim="trust.incorporationNumber"
                  :label="'Trust registration number*'"
                  :errors="errors"
                  :name="'trust_incorporation_number'"
                />
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col v-if="isEditForm" cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save'"
                  :small="true"
                  :callout="'right'"
                  @gradientButtonClicked="save"
                />
                <GradientButton
                  :title="'Remove Trust'"
                  :small="true"
                  :fill="false"
                  @gradientButtonClicked="deleteCheck = true"
                />
              </div>
            </v-col>

            <v-col v-else cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save And Continue'"
                  :small="true"
                  :callout="'right'"
                  :disabled="buttonLoading"
                  @gradientButtonClicked="save"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <DeleteDialog
        v-if="deleteCheck"
        :content="deleteDialogContent"
        :hide-delete-button="hideDeleteButton"
        @delete="deleteTrust"
        @dialogClosed="closeDialog"
      />
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import {
    CREATE_TRUST_MUTATION,
    UPDATE_TRUST_MUTATION,
    DELETE_TRUST_MUTATION,
  } from '../../constants/graphql/trusts';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'TrustDialog',
    components: {
      DeleteDialog,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      trustToUpdate: Object,
      existingTrusts: Array,
    },
    data() {
      return {
        trust: this.trustToUpdate || {},
        deleteCheck: false,
      };
    },
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      deleteDialogContent() {
        return {
          title: 'Remove ' + this.trust.name,
          text: this.deleteText,
          buttonText: 'Yes, Remove ' + this.toCapitalCase(this.trust.name),
        };
      },
      showExistingTrusts() {
        return this.existingTrusts && this.existingTrusts.length > 0;
      },
      isEditForm() {
        return this.trust && this.trust.id;
      },
      deleteText() {
        if (this.trustHasRemainderOfEstateSpecificPercentage) {
          return (
            'You have left a part of the remainder of your estate to ' +
            this.trust.name +
            ', please amend the Remainder of Estate section before deleting ' +
            this.trust.name
          );
        } else {
          return (
            'Are you sure you want to remove <b>' +
            this.trust.name +
            '</b>?<br><br><b>Warning:</b> Any bequests left to this trust will also be removed.'
          );
        }
      },
      hideDeleteButton() {
        return this.trustHasRemainderOfEstateSpecificPercentage ? true : false;
      },
      trustHasRemainderOfEstateSpecificPercentage() {
        return (
          this.trust.distributionSpecificPercentage ||
          this.trust.alternativeDistributionSpecificPercentage ||
          this.trust.distributionSpecificPercentageAlternatives.length > 0 ||
          this.trust.distributionSpecificPercentageAlternativeBeneficiaries
            ?.length > 0
        );
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen && this.trustToUpdate) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          this.trust = JSON.parse(JSON.stringify(this.trustToUpdate));
        }
      },
    },
    methods: {
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            if (this.trust.id) {
              this.updateTrust();
            } else {
              this.createTrust();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createTrust() {
        const trustInputDetails = {
          testatorId: this.testatorId,
          name: this.trust.name,
          incorporationNumber: this.trust.incorporationNumber,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_TRUST_MUTATION,
            variables: {
              trustInputDetails,
            },
            update: (cache, { data: { createTrust } }) => {
              this.updateCacheAfterCreateTrust(cache, createTrust);
            },
          })
          .then(response => {
            this.closeDialog();
            this.$emit('trustCreated', response.data.createTrust);
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterCreateTrust(cache, createTrust) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.trusts.push(createTrust);

        cache.writeQuery({ ...query, data: data });
      },
      updateTrust() {
        const trustInputDetails = {
          name: this.trust.name,
          incorporationNumber: this.trust.incorporationNumber,
        };
        const id = this.trust.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_TRUST_MUTATION,
            variables: {
              id,
              trustInputDetails,
            },
            update: (cache, { data: { updateTrust } }) => {
              this.updateCacheAfterUpdateTrust(cache, updateTrust);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterUpdateTrust(cache, updateTrust) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var trust = data.testator.trusts.find(
          trust => trust.id == updateTrust.id
        );
        var trust = updateTrust;

        cache.writeQuery({ ...query, data: data });
      },
      deleteTrust() {
        const id = this.trust.id;

        this.$apollo
          .mutate({
            mutation: DELETE_TRUST_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteTrust } }) => {
              this.updateCacheAfterDeleteTrust(cache, deleteTrust);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterDeleteTrust(cache, deleteTrust) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var trusts = data.testator.trusts;
        var index = trusts.findIndex(trust => trust.id == deleteTrust.id);
        trusts.splice(index, 1);

        cache.writeQuery({ ...query, data: data });
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.trust = {};
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
        this.deleteCheck = false;
      },
      selectExistingTrust(trust) {
        this.$emit('existingTrustSelected', trust);
        this.closeDialog();
      },
      trustDisabled(trust) {
        return trust.disabledMessage ? true : false;
      },
      toCapitalCase(str) {
        return str
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
    },
  };
</script>
<style lang="scss" scoped>
  .button-container {
    margin-bottom: 22px;
    * {
      margin-right: 15px;
    }
  }
</style>
